import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  AlertTriangle,
  ChevronsUpDown,
  Download,
  FileText,
  Info,
  Loader2,
  Minus,
  Plus,
} from "lucide-react"
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { downloadQuestionnaire } from "../../../../api/inquiryDraftApi"
import type { InquirySettingsStatus } from "../../../../api/supplierInquiriesApi"
import type {
  AttachmentFormat,
  InquiryDraftItem,
  Lang,
  Stage,
} from "./inquiryDraftMock"

// Lazy so the Excel viewer's heavy Fortune-Sheet deps load only when Excel is selected
// (PDF is the default). See InquiryExcelPreview.
const InquiryExcelPreview = lazy(() =>
  import("./InquiryExcelPreview").then((m) => ({
    default: m.InquiryExcelPreview,
  })),
)

// Fixed logical page width (px) the questionnaire renders at — comfortably wider than the
// A4 sheet (210mm ≈ 794px) so the grey "desk" margin shows. Fixed (not 100%) so the zoom
// wrapper can size itself to the SCALED width, which is what lets the canvas scroll to both
// edges when zoomed in past 100% instead of clipping them.
const PREVIEW_PAGE_WIDTH = 880

interface DraftPreviewPanelProps {
  bulk: boolean
  html: string | undefined
  isLoading: boolean
  inquiries: InquiryDraftItem[]
  previewId: string
  setPreviewId: (id: string) => void
  language: Lang
  // Chosen attachment format. Excel renders the backend workbook in the shared
  // Fortune-Sheet viewer (see InquiryExcelPreview) instead of the HTML/PDF iframe.
  format: AttachmentFormat
  stage: Stage
  // Whether the org's template settings resolved: a read failure shows a retry
  // below the toolbar.
  settingsStatus?: InquirySettingsStatus
  onRetrySettings?: () => void
  /** Below the toolbar: what of the org's settings would still go out as
   *  placeholder text (see DraftPlaceholderNotice). */
  notice?: ReactNode
}

export function DraftPreviewPanel({
  bulk,
  html,
  isLoading,
  inquiries,
  previewId,
  setPreviewId,
  language,
  format,
  stage,
  settingsStatus,
  onRetrySettings,
  notice,
}: DraftPreviewPanelProps) {
  const { translate } = useTranslation()
  const [zoom, setZoom] = useState(72)
  const [downloading, setDownloading] = useState(false)

  // Measure the rendered document height so the zoom wrapper can reserve the SCALED
  // height. A CSS `transform: scale()` shrinks the content visually but not its layout
  // box, so scaling an `h-full` iframe left ~(1-zoom) of dead space below the sheet and
  // forced an inner scroll. Reserving contentHeight*zoom lets the canvas scroll the whole
  // (possibly multi-page) document with no wasted space. srcDoc is same-origin, so the
  // content document is readable.
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [contentHeight, setContentHeight] = useState(1160)
  const measureContent = useCallback(() => {
    const doc = iframeRef.current?.contentDocument
    const h = doc?.documentElement?.scrollHeight ?? 0
    if (h > 0) setContentHeight(h)
  }, [])
  // Re-measure when the document changes. onLoad covers the srcDoc reload, but a small
  // deferred pass catches late layout (fonts, article-row swaps that reuse the frame).
  useEffect(() => {
    const id = window.setTimeout(measureContent, 60)
    return () => window.clearTimeout(id)
  }, [html, measureContent])

  // Resolve the inquiry actually on screen. previewId can point at a row that's gone
  // after a refetch, so fall back to the first — and use THIS id (not the raw previewId)
  // for both the download and the Excel fetch, or they'd target the wrong supplier.
  const previewed = inquiries.find((i) => i.id === previewId) ?? inquiries[0]
  const name = previewed?.supplierName ?? ""
  const isExcel = format === "excel"
  // "none" (a declaration-review Rückfrage, or its reminder) sends no attachment at
  // all: there is nothing to download, and no format tab to switch between. The
  // questionnaire/email preview below is unaffected and still renders.
  const hasAttachment = format !== "none"

  async function handleDownload() {
    if (!previewed || downloading) return
    setDownloading(true)
    try {
      await downloadQuestionnaire(previewed.id, {
        stage,
        lang: language,
        format,
      })
    } finally {
      setDownloading(false)
    }
  }
  // Signature of the PERSISTED article set — the Excel preview re-fetches when this
  // changes (the workbook is server-rendered from saved articles, not swapped live).
  const articleSig = (previewed?.articles ?? []).map((a) => a.id).join(",")
  // Same for the regulation pick: the stored keys, and what they resolve to (a draft
  // following Settings has no keys, and changes when Settings do).
  const scopeSig = [
    previewed?.regulationKeys?.join(",") ?? "",
    previewed?.askedRegulations?.join(",") ?? "",
  ].join("|")

  return (
    <div className="flex h-full flex-col bg-grey3">
      {/* toolbar */}
      <div className="relative flex items-center gap-3 border-b border-grey4 px-4 py-2.5">
        <span className="flex items-center gap-1.5 text-[12px] text-grey5">
          <FileText className="h-3.5 w-3.5" />
          {translate(
            isExcel
              ? "MC_INQ_DRAFT_PREVIEW_FILENAME_XLSX"
              : "MC_INQ_DRAFT_PREVIEW_FILENAME",
          )}
        </span>
        {bulk && (
          <>
            <span className="h-4 w-px bg-grey4" />
            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex h-8 max-w-[220px] items-center gap-2 rounded-md border border-grey4 bg-grey3 px-2.5 text-[11.5px] text-white hover:border-grey5">
                <span className="text-grey5">
                  {translate("MC_INQ_DRAFT_PREVIEWING")}
                </span>
                <span className="truncate">{name}</span>
                <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-grey5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {inquiries.map((s) => (
                  <DropdownMenuItem
                    key={s.id}
                    onClick={() => setPreviewId(s.id)}
                  >
                    {s.supplierName}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
        <span className="flex-1" />
        <div className="flex items-center gap-1.5">
          {/* Zoom applies to the PDF/HTML preview only — Excel shows a static summary. */}
          {!isExcel && (
            <>
              <button
                type="button"
                aria-label={translate("MC_INQ_DRAFT_ZOOM_OUT")}
                onClick={() => setZoom((z) => Math.max(30, z - 10))}
                className="text-grey5 hover:text-white"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="font-mono text-[11px] text-grey5">{zoom}%</span>
              <button
                type="button"
                aria-label={translate("MC_INQ_DRAFT_ZOOM_IN")}
                onClick={() => setZoom((z) => Math.min(150, z + 10))}
                className="text-grey5 hover:text-white"
              >
                <Plus className="h-4 w-4" />
              </button>
            </>
          )}
          {hasAttachment && (
            <button
              type="button"
              aria-label={translate("MC_INQ_DRAFT_DOWNLOAD")}
              className="ml-2 text-grey5 hover:text-white disabled:opacity-50"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? (
                // The PDF is rendered by the Gotenberg service; the first request after
                // idle can take ~20-25s (Lambda cold start), so show a spinner (not just a
                // dimmed icon) to make the wait read as "working".
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* What would still go out as placeholder text, each with its fix. Not keyed
          to `settingsStatus === "unconfigured"`: that ends with the org's first save
          of ANY setting, while the signature may still read "[Your name]". */}
      {notice}
      {/* A genuine settings read failure, retryable. */}
      {settingsStatus === "unavailable" && (
        <div className="flex items-center gap-2 border-b border-grey4 bg-grey2 px-4 py-2 text-[12px] text-warning">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1">
            {translate("MC_INQ_SETTINGS_UNAVAILABLE")}
          </span>
          {onRetrySettings && (
            <button
              type="button"
              onClick={onRetrySettings}
              className="font-medium text-yellow1 hover:underline"
            >
              {translate("MC_DECL_BTN_RETRY")}
            </button>
          )}
        </div>
      )}

      {/* canvas — scrolls via this div (using the app's global scrollbar, same as the
          compose panel) rather than an inner iframe scrollbar; see the zoom wrapper below. */}
      <div
        className={`relative flex-1 bg-grey2 ${isExcel ? "overflow-hidden" : "overflow-auto"}`}
      >
        {isLoading && !isExcel && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-grey2/80">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-grey4 border-t-yellow1" />
          </div>
        )}
        {isExcel ? (
          // Excel renders the real backend workbook (all 12 framework sheets) in the shared
          // read-only viewer, tracking the SAVED article scope (edits appear after autosave —
          // see InquiryExcelPreview / articleSig).
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-grey4 border-t-yellow1" />
              </div>
            }
          >
            <InquiryExcelPreview
              inquiryId={previewed?.id ?? ""}
              stage={stage}
              lang={language}
              articleSig={articleSig}
              scopeSig={scopeSig}
              dueAt={previewed?.dueAt ?? null}
            />
          </Suspense>
        ) : html ? (
          // Wrapper is sized to the SCALED page (width AND height) and clips the transform's
          // phantom (unscaled) layout box. Because it takes the scaled width, zooming past
          // 100% makes it wider than the canvas, so the overflow-auto canvas scrolls to both
          // edges (no clipping); zooming out centers it via mx-auto with no dead space.
          <div
            className="mx-auto my-5 overflow-hidden"
            style={{
              width: (PREVIEW_PAGE_WIDTH * zoom) / 100,
              height: (contentHeight * zoom) / 100,
            }}
          >
            <iframe
              ref={iframeRef}
              srcDoc={html}
              onLoad={measureContent}
              sandbox="allow-same-origin"
              title={translate("MC_INQ_DRAFT_PREVIEW_FILENAME")}
              style={{
                width: PREVIEW_PAGE_WIDTH,
                height: contentHeight,
                transform: `scale(${zoom / 100})`,
                transformOrigin: "top left",
              }}
              className="block border-0 bg-white"
            />
          </div>
        ) : (
          !isLoading && (
            <div className="flex h-full items-center justify-center text-sm text-grey5">
              {translate("MC_INQ_DRAFT_PREVIEW_UNAVAILABLE")}
            </div>
          )
        )}
      </div>

      {/* footer */}
      <div className="flex items-center gap-2 border-t border-grey4 bg-grey2 px-4 py-2.5 text-[11.5px] text-grey5">
        <Info className="h-3.5 w-3.5 shrink-0" />
        {isExcel
          ? translate("MC_INQ_DRAFT_FOOT_EXCEL")
          : bulk
            ? translate("MC_INQ_DRAFT_FOOT_BULK", { name })
            : translate("MC_INQ_DRAFT_FOOT_SINGLE")}
      </div>
    </div>
  )
}
